import api from './api';

export interface ClienteCredito {
  id: number;
  cliente_id: number;
  tipo: 'cadastro' | 'recorrente' | 'bonificacao';
  valor: number;
  saldo: number;
  cliente_origem_id?: number;
  nivel?: number;
  fatura_id?: number;
  utilizado: boolean;
  data_utilizacao?: string;
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
  cliente?: {
    id: number;
    nome: string;
  };
  cliente_origem?: {
    id: number;
    nome: string;
  };
}

export interface EstatisticasCreditos {
  totalDisponivel: number;
  totalUtilizado: number;
  totalGerado: number;
  porTipo: {
    cadastro: number;
    recorrente: number;
  };
  creditosAtivos: number;
  percentualUtilizado: string;
}

export interface SimulacaoCreditos {
  valorOriginal: number;
  totalDesconto: number;
  valorFinal: number;
  creditosUsados: Array<{
    id: number;
    tipo: 'cadastro' | 'recorrente' | 'bonificacao';
    nivel?: number;
    saldoOriginal: number;
    valorUsado: number;
    saldoRestante: number;
    cliente_origem?: {
      id: number;
      nome: string;
    };
  }>;
  economiaPercentual: string;
}

export interface PaginatedResponse<T> {
  creditos?: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface HistoricoFilters {
  page?: number;
  limit?: number;
  tipo?: 'cadastro' | 'recorrente' | 'bonificacao';
  utilizado?: boolean;
}

class ClienteCreditoService {

  /**
   * Busca créditos disponíveis de um cliente
   */
  async getCreditosDisponiveis(clienteId: number): Promise<ClienteCredito[]> {
    const response = await api.get(`/cliente-creditos/${clienteId}/disponiveis`);
    return response as unknown as ClienteCredito[];
  }

  /**
   * Busca créditos utilizados em uma fatura específica
   */
  async getCreditosUtilizados(faturaId: number): Promise<ClienteCredito[]> {
    try {
      const response: any = await api.get(`/cliente-creditos/fatura/${faturaId}/utilizados`);
      if (response && Array.isArray(response.creditos)) {
        return response.creditos;
      }
      if (response && Array.isArray(response.data?.creditos)) {
        return response.data.creditos;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      console.error('Erro ao buscar créditos utilizados:', error);
      return [];
    }
  }

  /**
   * Busca histórico completo de créditos de um cliente
   */
  async getHistoricoCreditos(
    clienteId: number, 
    filters: HistoricoFilters = {}
  ): Promise<PaginatedResponse<ClienteCredito>> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.utilizado !== undefined) params.append('utilizado', filters.utilizado.toString());

    const response = await api.get(`/cliente-creditos/${clienteId}/historico?${params.toString()}`);
    return response as unknown as PaginatedResponse<ClienteCredito>;
  }

  /**
   * Calcula total de créditos disponíveis de um cliente
   */
  async getTotalCreditosDisponiveis(clienteId: number): Promise<{ total: number }> {
    const response = await api.get(`/cliente-creditos/${clienteId}/total`);
    return response as unknown as { total: number };
  }

  /**
   * Simula aplicação de créditos em uma fatura
   */
  async simularAplicacaoCreditos(
    clienteId: number, 
    valorFatura: number
  ): Promise<SimulacaoCreditos> {
    const response = await api.post(`/cliente-creditos/${clienteId}/simular`, {
      valorFatura
    });
    return response as unknown as SimulacaoCreditos;
  }

  /**
   * Busca estatísticas resumidas de créditos por tipo
   */
  async getEstatisticasCreditos(clienteId: number): Promise<EstatisticasCreditos> {
    const response = await api.get(`/cliente-creditos/${clienteId}/estatisticas`);
    return response as unknown as EstatisticasCreditos;
  }

  /**
   * Busca detalhes de um crédito específico
   */
  async getCreditoDetalhes(creditoId: number): Promise<ClienteCredito> {
    const response = await api.get(`/cliente-creditos/detalhes/${creditoId}`);
    return response as unknown as ClienteCredito;
  }

  /**
   * Formata tipo de crédito para exibição
   */
  formatTipoCredito(tipo: 'cadastro' | 'recorrente' | 'bonificacao' | 'pagamento_excedente' | string): string {
    switch (tipo) {
      case 'cadastro': return 'Cadastro';
      case 'recorrente': return 'Recorrente';
      case 'bonificacao': return 'Bonificação';
      case 'pagamento_excedente': return 'Pagamento Excedente';
      default: return String(tipo);
    }
  }

  /**
   * Retorna cor do chip baseado no tipo de crédito
   */
  getTipoCreditoColor(tipo: 'cadastro' | 'recorrente' | 'bonificacao'): 'success' | 'info' | 'warning' {
    switch (tipo) {
      case 'cadastro': return 'success';
      case 'recorrente': return 'info';
      case 'bonificacao': return 'warning';
      default: return 'info';
    }
  }

  /**
   * Retorna ícone baseado no tipo de crédito
   */
  getTipoCreditoIcon(tipo: 'cadastro' | 'recorrente' | 'bonificacao'): string {
    switch (tipo) {
      case 'cadastro': return 'person_add';
      case 'recorrente': return 'repeat';
      case 'bonificacao': return 'card_giftcard';
      default: return 'credit_card';
    }
  }

  /**
   * Formata valor monetário
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formata data para exibição
   */
  formatDate(date: string): string {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  }

  /**
   * Calcula tempo decorrido desde a criação do crédito
   */
  getTempoDecorrido(createdAt: string): string {
    const agora = new Date();
    const criacao = new Date(createdAt);
    const diffMs = agora.getTime() - criacao.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Hoje';
    } else if (diffDays === 1) {
      return 'Ontem';
    } else if (diffDays < 30) {
      return `${diffDays} dias atrás`;
    } else if (diffDays < 365) {
      const meses = Math.floor(diffDays / 30);
      return `${meses} ${meses === 1 ? 'mês' : 'meses'} atrás`;
    } else {
      const anos = Math.floor(diffDays / 365);
      return `${anos} ${anos === 1 ? 'ano' : 'anos'} atrás`;
    }
  }

  /**
   * Calcula percentual de utilização de um crédito
   */
  getPercentualUtilizado(credito: ClienteCredito): number {
    if (credito.valor === 0) return 0;
    const utilizado = credito.valor - credito.saldo;
    return (utilizado / credito.valor) * 100;
  }

  /**
   * Verifica se um crédito está próximo do vencimento (se houver regra)
   */
  isProximoVencimento(createdAt: string, diasValidade: number = 365): boolean {
    const agora = new Date();
    const criacao = new Date(createdAt);
    const vencimento = new Date(criacao.getTime() + (diasValidade * 24 * 60 * 60 * 1000));
    const diffMs = vencimento.getTime() - agora.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    return diffDays <= 30 && diffDays > 0; // Próximo dos últimos 30 dias
  }

  /**
   * Agrupa créditos por origem (cliente que indicou)
   */
  agruparCreditosPorOrigem(creditos: ClienteCredito[]): Record<number | 'sem_origem', ClienteCredito[]> {
    return creditos.reduce((acc, credito) => {
      const origemId = credito.cliente_origem_id || 'sem_origem';
      if (!acc[origemId]) {
        acc[origemId] = [];
      }
      acc[origemId].push(credito);
      return acc;
    }, {} as Record<number | 'sem_origem', ClienteCredito[]>);
  }

  /**
   * Calcula resumo por nível
   */
  getResumoPorNivel(creditos: ClienteCredito[]): Array<{
    nivel: number;
    quantidade: number;
    totalValor: number;
    totalSaldo: number;
  }> {
    const resumo = creditos.reduce((acc, credito) => {
      const nivel = credito.nivel || 0; // Usar 0 como padrão se nível não for definido
      if (!acc[nivel]) {
        acc[nivel] = {
          nivel,
          quantidade: 0,
          totalValor: 0,
          totalSaldo: 0
        };
      }
      
      acc[nivel].quantidade++;
      acc[nivel].totalValor += credito.valor;
      acc[nivel].totalSaldo += credito.saldo;
      
      return acc;
    }, {} as Record<number, any>);

    return Object.values(resumo).sort((a, b) => a.nivel - b.nivel);
  }

  /**
   * Valida valor para simulação
   */
  validateSimulacaoValue(valor: number): string[] {
    const errors: string[] = [];

    if (valor <= 0) {
      errors.push('Valor deve ser maior que zero');
    }

    if (valor > 9999999.99) {
      errors.push('Valor muito alto para simulação');
    }

    return errors;
  }

  /**
   * Busca todos os créditos com filtros e paginação
   */
  async getAllCreditos(
    filters: any = {},
    page: number = 0,
    limit: number = 25,
    searchTerm: string = ''
  ): Promise<PaginatedResponse<ClienteCredito>> {
    const params = new URLSearchParams();
    
    // Converter de base 0 para base 1 (backend usa base 1)
    params.append('page', (page + 1).toString());
    params.append('limit', limit.toString());
    
    if (searchTerm) params.append('search', searchTerm);
    if (filters.tipo) params.append('tipo', filters.tipo);
    if (filters.status) {
      if (filters.status === 'disponivel') {
        params.append('utilizado', 'false');
        params.append('saldo_gt', '0');
      } else if (filters.status === 'utilizado') {
        params.append('utilizado', 'true');
      }
    }
    if (filters.nivel && filters.nivel !== '') params.append('nivel', filters.nivel);
    if (filters.dataInicio) params.append('data_inicio', filters.dataInicio.toISOString());
    if (filters.dataFim) params.append('data_fim', filters.dataFim.toISOString());

    const response = await api.get(`/cliente-creditos?${params.toString()}`);
    return response as unknown as PaginatedResponse<ClienteCredito>;
  }

  /**
   * Busca estatísticas gerais de todos os créditos
   */
  async getEstatisticasGerais(): Promise<EstatisticasCreditos> {
    const response = await api.get('/cliente-creditos/estatisticas-gerais');
    return response as unknown as EstatisticasCreditos;
  }

  /**
   * Cria um novo crédito
   */
  async createCredito(creditoData: Omit<ClienteCredito, 'id' | 'createdAt' | 'updatedAt'>): Promise<ClienteCredito> {
    const response = await api.post('/cliente-creditos', creditoData);
    return response as unknown as ClienteCredito;
  }
}

export default new ClienteCreditoService(); 